import React, { useRef, useContext, Fragment } from 'react';
import { find, omit } from 'lodash-es';
import { IPdpData, IVariant, TUserSelection, UpdateSelectionFunc } from '../../types/PDP';
import { UrgencyMessage } from '../ScarcityMessaging/UrgencyMessage';
import { computeQuantityRemaining } from '../ScarcityMessaging/InitUrgencyMessage';
import { itemIsAccessory } from '../../../../utils/accessories';
import { computeProductOptionLabel, hasAdvanceOrderAndPreorderTags, isEligibleForPreorder } from '../../utils';
import { ProductContext } from '../../ProductContext';
import { FindMySizeModal } from '../../../_shared/ui-components/FIndMySIze/FindMySizeModal';
import ButtonsVariantSelector from './ButtonsVariantSelector';
import GiftCardRecipientUI from './GiftCardRecipientUI';
import { checkIfProductIsPhasedOut, getSizeSelectorItems } from './utils';
import SizeGuideToggle from './SizeGuideToggle';
import { SwatchVariantSelector } from './SwatchVariantSelector';
import { computeProductOptionsWithMetadata } from './helpers';
import { useMediaQuery } from '@material-ui/core';
import { MUI_MOBILE_BREAKPOINT } from 'src/scripts/utils/constants';
import FitAndDescriptionToggle from './FitAndDescriptionToggle';
import QuickFit from '../QuickFit';
import { extractPurchaseLimitTagData } from 'src/scripts/templates/_shared/ui-components/PurchaseLimitItem/PurchaseLimitMessaging';
import './styles.scss';
import NotifyMe from '../NotifyMe';
import { getVariantsBySelections } from '../product-utils';
import { hasHiddenInventoryTag } from 'src/scripts/utils/plp-pdp-shared';
import { checkIfItemCanBeSoldOut, preorderDateRangeCopy } from 'src/scripts/utils/helpers';
import { notNil } from 'src/scripts/utils/typescriptUtils';
import { LengthTogglePdp } from '../LengthToggle';

export interface VariantSelectionsProps {
  pdpData: IPdpData;
  userSelections: TUserSelection[];
  updateSelection: UpdateSelectionFunc;
  displayPrice: number;
  actualPrice: number;
}

export const VariantSelections = (props: VariantSelectionsProps) => {
  const { pdpData, userSelections, updateSelection, displayPrice, actualPrice } = props;

  const { isSelectionError, addToCartCount } = useContext(ProductContext);

  const { products, isASet } = pdpData;

  const dropdownRefs = useRef<React.MutableRefObject<HTMLDivElement | null>[]>(
    products.map((index) => React.createRef())
  );
  const isMobile = useMediaQuery(MUI_MOBILE_BREAKPOINT, { noSsr: true });

  // ? [REVISIT] we can't have 400 line functions
  const variantSelectionUI = products.map((product, productIndex) => {
    const { variants } = product;
    const productTypeInLowerCase = product.productType.toLowerCase();

    const shouldShowFindMySize = productTypeInLowerCase.startsWith('women') && productTypeInLowerCase.includes(':bras');
    const shouldShowSizeGuideToggle =
      !productTypeInLowerCase.includes(':swimwear:') &&
      !productTypeInLowerCase.includes(':hair management:') &&
      !productTypeInLowerCase.includes('gloves') &&
      !productTypeInLowerCase.includes('hats') &&
      (productTypeInLowerCase.startsWith('women') ||
        productTypeInLowerCase.startsWith('men') ||
        productTypeInLowerCase.includes(':shoes:') ||
        productTypeInLowerCase.includes(':socks:'));
    //@ts-expect-error
    const productUserSelection: TUserSelection = userSelections[productIndex];
    const productDataForColor = product.productDataByColorName[productUserSelection.Color || ''];
    const { handle, baseTitle: title, tags } = productDataForColor || {};
    //if isMobile is false or not passed in we assume not mobile..
    let itemIsProbablyAnAccessory: boolean = itemIsAccessory(pdpData);

    const optionsWithMetadata = computeProductOptionsWithMetadata(product, tags);
    const validOptionsToRender = optionsWithMetadata.filter(({ allValuesInBlackList }) => !allValuesInBlackList);

    const quickFitText = productDataForColor?.attributes?.find((obj) => obj?.key === 'quickFit')?.value;

    return (
      <div
        key={productIndex}
        id="variant-selector"
        className={`product__right__description d-flex flex-column ${isASet ? 'sets-description' : ''}`}
        data-set={isASet}
      >
        {validOptionsToRender.map((option, index, array) => {
          const isLastIndex = index === array.length - 1;
          //@ts-ignore: temporary artifact of not having real TS & don't to make type UpdateSelectionFunc's 'optionName': string (yet) // ? [T900]
          const optionName: 'Color' | 'Size' = option.optionName;
          const { optionValues, shouldRenderSelectionsUI, optionNumber } = option;
          const isColorOption = optionName === 'Color';

          const optionLabel = computeProductOptionLabel({
            optionName,
            productType: product.productType,
            tags,
            optionNumber,
          });

          const errorMessage = `Please select ${
            optionLabel.toLowerCase() === 'amount' ? 'an' : 'a'
          } ${optionLabel.toLowerCase()}.`;

          let currentVariant = !isColorOption
            ? find(variants, {
                // availableForSale: true,
                selectionOptions: {
                  [optionName]: productUserSelection[optionName],
                  Color: productUserSelection.Color,
                },
              })
            : find(variants, {
                availableForSale: true,
                selectionOptions: {
                  [optionName]: productUserSelection[optionName],
                },
              }) ||
              find(variants, {
                selectionOptions: {
                  [optionName]: productUserSelection[optionName],
                },
              });

          let quantityRemaining: number = computeQuantityRemaining(currentVariant);
          const colorVariants = variants.filter(
            (variant) => variant.selectionOptions.Color === productUserSelection.Color
          );
          const isAdvanceOrder = hasAdvanceOrderAndPreorderTags(tags) && !currentVariant;
          const estimatedShipStartDate = currentVariant?.estimatedShipStartDate?.value;
          const isPreorder = isEligibleForPreorder(quantityRemaining, tags, estimatedShipStartDate);
          let preorderCopy = null;
          if (isPreorder) preorderCopy = preorderDateRangeCopy(estimatedShipStartDate);
          if (isAdvanceOrder) preorderCopy = preorderDateRangeCopy(getEarliestShipStartDate(colorVariants));

          const isEGiftCard = variants.some((variant) => variant.sku === 'GCSHOPIFY');

          if (!shouldRenderSelectionsUI) {
            const displayValue = optionValues?.[0];
            return (
              displayValue && (
                <>
                  <p style={{ display: 'flex' }}>
                    <span className="small-p black">
                      {optionLabel}: {displayValue}
                    </span>
                  </p>
                  {preorderCopy && isLastIndex && (
                    <div className="preorder-info">
                      <span>Expected to ship: </span>
                      <span>{`${preorderCopy}`}</span>
                      <p>You will be charged at checkout and notified as soon as your order ships.</p>
                    </div>
                  )}
                </>
              )
            );
          }

          const unlimitedInventory = !checkIfItemCanBeSoldOut(tags);
          const purchaseLimitTagData = extractPurchaseLimitTagData(tags);
          const selectedVariants = getVariantsBySelections(pdpData, userSelections);
          const isProductPhasedOut = checkIfProductIsPhasedOut(tags || []);
          const shouldHideInventory = hasHiddenInventoryTag(pdpData?.pdpProductShell.tags);
          const shouldShowNotifyMe =
            !isProductPhasedOut && (!selectedVariants[productIndex]?.availableForSale || shouldHideInventory);
          return (
            <Fragment key={`${productIndex}-${optionName}`}>
              {isColorOption && (
                <div className={`${isASet ? 'set' : ''} child-product__wrapper`} data-set={isASet}>
                  {isASet && (
                    <a
                      className="child-product__link"
                      tabIndex={0}
                      href={`/products/${handle}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        className="child-product__image"
                        // ? [T900]
                        src={
                          (find(variants, { selectionOptions: omit(productUserSelection, 'Size') }) || variants[0])
                            ?.imageUrl
                        }
                        alt={title}
                      />
                    </a>
                  )}
                  <div
                    className={`variant__swatches variant__swatches${isASet ? `--set` : ''} variant__swatches--color`}
                  >
                    {isASet && (
                      <a
                        className="child-product__link"
                        tabIndex={0}
                        href={`/products/${handle}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <h2 className="product-name product-name--set">{title}</h2>
                      </a>
                    )}
                    <SwatchVariantSelector
                      optionValues={optionValues}
                      displayPrice={displayPrice}
                      actualPrice={actualPrice}
                      isASet={isASet}
                      updateSelection={updateSelection}
                      productUserSelection={productUserSelection}
                      optionName={optionName}
                      product={product}
                      productIndex={productIndex}
                    />
                  </div>
                </div>
              )}

              {index === 0 ? (
                <>
                  {productDataForColor ? <LengthTogglePdp colorwayProductData={productDataForColor} /> : null}
                  {quickFitText ? <QuickFit quickFitText={quickFitText} /> : null}
                  {!isASet && (shouldShowSizeGuideToggle || shouldShowFindMySize || isMobile) && (
                    <div className="variant-selector-modal-triggers">
                      {shouldShowSizeGuideToggle && <SizeGuideToggle />}
                      {shouldShowFindMySize && <FindMySizeModal />}
                      {isMobile && <FitAndDescriptionToggle shouldShowOnlyDescription={isEGiftCard} />}
                    </div>
                  )}
                </>
              ) : null}

              {!isColorOption ? (
                <div className={'options-wrapper options-wrapper--size'}>
                  <>
                    <div
                      className={`size-selector ${productIndex}`}
                      ref={dropdownRefs.current[productIndex]}
                      data-index={productIndex} // For sets: to open only related dropdown
                    >
                      <ButtonsVariantSelector
                        productIndex={productIndex}
                        dropdownItemsData={getSizeSelectorItems(
                          optionName,
                          optionValues,
                          variants,
                          tags,
                          product.productType,
                          productUserSelection
                        )}
                        product={product}
                        userSelection={productUserSelection}
                        optionName={optionName}
                        updateSelection={updateSelection}
                        isASet={isASet}
                        purchaseLimitTagData={purchaseLimitTagData}
                        error={isSelectionError && !productUserSelection[optionName]}
                      />
                    </div>
                    {isSelectionError && !productUserSelection[optionName] ? (
                      <p className={`small-p size-selecion-error`}>{errorMessage}</p>
                    ) : null}
                    {isEGiftCard && <GiftCardRecipientUI key={addToCartCount} />}
                    {isASet && shouldShowNotifyMe ? (
                      <div className={`notify-enable notify-form-container-${productIndex}`}>
                        <NotifyMe
                          customerEmail={pdpData.customer.email}
                          product={product}
                          userSelection={productUserSelection}
                          variant={selectedVariants[productIndex]}
                          itemIsProbablyAnAccessory={itemIsProbablyAnAccessory}
                          uniqueIDforCheckbox={`notify-enable-checkbox-${productIndex}`}
                          shouldShowNotifyMe={true}
                          tags={tags}
                        />
                      </div>
                    ) : null}
                  </>
                </div>
              ) : null}
              {!unlimitedInventory && isColorOption && itemIsProbablyAnAccessory ? (
                <UrgencyMessage
                  quantityLeft={quantityRemaining}
                  className="buttonSelector"
                  style={{ color: 'var(--primary)' }}
                />
              ) : null}
              {preorderCopy && isLastIndex && (
                <div className="preorder-info">
                  <span>Expected to ship: </span>
                  <span>{`${preorderCopy}`}</span>
                  <p>You will be charged at checkout and notified as soon as your order ships.</p>
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
    );
  });

  return <div>{variantSelectionUI}</div>;
};

const getEarliestShipStartDate = (variants: IVariant[]): string | null => {
  const dates = variants
    .map((variant) => (variant.availableForSale ? variant.estimatedShipStartDate?.value : null))
    .filter(notNil)
    .map((dateString) => new Date(dateString))
    .sort((a, b) => a.getTime() - b.getTime());

  const earliestDate = dates[0];
  return earliestDate ? earliestDate.toISOString() : null;
};
